import { camelCase } from "lodash";

import { sortByKey } from "lib/compare";
import { isCommercialUser } from "lib/reports";

export enum ReportType {
  FAVOURITES = "favourites",
  AGENCIES = "agencies",
  AGENTS = "agents",
  BRANCHES = "branches",
  BUYERS = "buyers",
  CARDS = "cards",
  VENDORS = "vendors",
  STUD = "stud",
  SALE_FILES = "sale_files",
  NLIS = "nlis",
  SALE_EXPORT = "sale_export",
  SCANS = "scans",
  SALEYARD = "saleyard",
}

export enum OutputFormat {
  PDF = "pdf",
  CSV = "csv",
  XLS = "xls",
}

export enum ClientAgingPeriod {
  SEVEN_DAYS = "seven_days",
  THIRTY_DAYS = "thirty_days",
  MONTHLY = "monthly",
}

// Business relation keys (e.g. "stock_agent") are also valid group bys.
export enum ClientAgingGroupBy {
  NONE = "none",
  BRANCH = "branch",
  LABEL = "label",
}

export enum ClientAgingLayout {
  SUMMARY = "summary",
  DETAIL = "detail",
}

export enum ClientAgingClientType {
  COMBINED = "combined",
  DEBTORS = "debtors",
  CREDITORS = "creditors",
}

export enum ClientAgingOrderBy {
  NAME = "name",
  CODE = "code",
  TOTAL = "total",
  OLDEST = "oldest",
}

export const ALL_REPORT_TYPES = Object.values(ReportType);

export const REPORT_DISPLAY_NAME = {
  [ReportType.FAVOURITES]: "Favourites",
  [ReportType.AGENCIES]: "Agencies",
  [ReportType.AGENTS]: "Agents",
  [ReportType.BRANCHES]: "Branches",
  [ReportType.BUYERS]: "Buyers",
  [ReportType.CARDS]: "Cards",
  [ReportType.VENDORS]: "Vendors",
  [ReportType.SALE_FILES]: "Sale Files",
  [ReportType.NLIS]: "NLIS",
  [ReportType.SALE_EXPORT]: "Sale Export",
  [ReportType.SALEYARD]: "Saleyard",
  [ReportType.STUD]: "Stud",
} as const satisfies Partial<Record<ReportType, string>>;

export const VENDOR_DETAILS_REPORT_NAME = "Vendor details report";
export const BUYER_PURCHASE_DETAILS_REPORT_NAME = "Buyer purchase check";
export const BUYER_DELIVERY_DETAILS_REPORT_NAME = "Buyer delivery details";

export enum ReportQueryParam {
  livestockSaleIdIn = "livestocksale-id__in",
  livestockSaleDateGte = "livestocksale-date__gte",
  livestockSaleDateLte = "livestocksale-date__lte",
  livestockSaleSaleTypeNameIn = "livestocksale-sale_type__name__in",
  livestockSaleSaleSubTypeIdIn = "livestocksale-sale_sub_type_id__in",
  startDate = "start_date",
  endDate = "end_date",
  businessId = "business_id",
  groupBys = "group_bys",
  speciesId = "species_id",
  onlySold = "only_sold",
  outputFormat = "output_format",
  relatedFrom = "related_from__cbid",
  relatedTo = "related_to__cbid",
  showStockDetails = "show_stock_details",
  asAtDate = "as_at_date",
  buckets = "buckets",
  bucketCount = "bucket_count",
  groupBy = "group_by",
  orderBy = "order_by",
  layout = "layout",
  clientType = "client_type",
  showContactDetails = "show_contact_details",
  labelIds = "label_ids",
  relationType = "relation_type",
  relatedBusinessId = "related_business_id",
  documentStatusIn = "document_status__in",
}

function makeQueryParamsCamelCase(params: string[]): Record<string, string> {
  return params.reduce<Record<string, string>>((paramMap, param) => {
    paramMap[param] = camelCase(param.replace(/[^a-zA-Z0-9_]/, "_")).replace(
      "livestocksale",
      "livestockSale",
    );
    return paramMap;
  }, {});
}

export enum ReportDriver {
  AGENT = "isAgentDriven",
  AGENCY = "isAgencyDriven",
  BRANCH_AND_AGENCY = "isBranchAndAgencyDriven",
  BUYER_AND_SALE_ROUND = "isBuyerAndSaleRoundDriven",
  BIDDER = "isBidderDriven",
  BUYER = "isBuyerDriven",
  BUYER_BUYER_WAY = "isBuyerBuyerWayDriven",
  BUYER_DESTINATION_PIC = "isBuyerDestinationPicDriven",
  BUYER_NAME_RANGE = "isBuyerNameRangeDriven",
  CONSIGNED_FROM = "isConsignedFromDriven",
  DEPLOYMENT = "isDeploymentDriven",
  MULTI_SALE = "isMultiSale",
  RULEBOOK = "isRulebookDriven",
  SALE_ROUND = "isSaleRoundDriven",
  SALE_ROUND_AND_AGENCY = "isSaleRoundAndAgencyDriven",
  VENDOR = "isVendorDriven",
}

interface ReportConfig extends Partial<Record<ReportDriver, boolean>> {
  fileType: "pdf" | "csv" | "txt" | "zip";
  pathTemplate: string;
  showToAgents: boolean;
  showToBuyers: boolean;
  showToSaleyardAdmins: boolean;
  showToVendors: boolean;
  slug: string;
  title: string;
  queryMap?: Record<string, string | boolean>;
  queryMapBulk?: Record<string, string | boolean>;
  tab?: "buyers" | "saleyard" | null;
  showOnBuyerCard?: boolean;
  showBillingRunSummary?: boolean;
  outputFormatOptions?: OutputFormat[];
  getAllowRenderReport?: (userRole: {
    type?: string;
    is_commercial_user?: boolean;
  }) => boolean;
  allowAttachedToBillingDocumentEmail?: boolean;
}

export const Reports: Record<string, ReportConfig> = {
  NVDImagesPDFView: {
    fileType: "pdf",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: true,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isRulebookDriven: false,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate:
      "/reports/saleyards/{saleyardName}/nvd-images/{livestockSaleId}/{buyerId}/",
    showToAgents: true,
    showToBuyers: true,
    showToSaleyardAdmins: true,
    showToVendors: false,
    queryMap: {
      user_role: "userRole",
      buyer_way: "buyerWay",
    },
    slug: "NVDImagesPDFView",
    title: "NVD & Attachments",
    showOnBuyerCard: true,
  },
  BuyerReconciliationPDFView: {
    fileType: "pdf",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: true,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isRulebookDriven: false,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate:
      "/reports/saleyards/{saleyardName}/buyer-reconciliation/{livestockSaleId}/{buyerId}/",
    showToAgents: true,
    showToBuyers: true,
    showToSaleyardAdmins: true,
    showToVendors: false,
    queryMap: {
      user_role: "userRole",
      buyer_way: "buyerWay",
    },
    slug: "BuyerReconciliationPDFView",
    title: "Buyer Reconciliation Report",
    showOnBuyerCard: true,
    getAllowRenderReport: isCommercialUser,
  },
  BuyerPurchaseDetailBySexPDFView: {
    fileType: "pdf",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: true,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isRulebookDriven: false,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate:
      "/reports/saleyards/{saleyardName}/buyer-purchase-detail-by-sex/{livestockSaleId}/{buyerId}/",
    showToAgents: true,
    showToBuyers: true,
    showToSaleyardAdmins: true,
    showToVendors: false,
    queryMap: {
      user_role: "userRole",
      buyer_way: "buyerWay",
    },
    slug: "BuyerPurchaseDetailBySexPDFView",
    title: "Buyer Purchase Detail (by Sex)",
    showOnBuyerCard: true,
    getAllowRenderReport: isCommercialUser,
  },
  BuyerPurchaseDetailPDFView: {
    fileType: "pdf",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: true,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isRulebookDriven: false,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate:
      "/reports/saleyards/{saleyardName}/buyer-purchase-detail/{livestockSaleId}/{buyerId}/",
    showToAgents: true,
    showToBuyers: true,
    showToSaleyardAdmins: true,
    showToVendors: false,
    queryMap: {
      user_role: "userRole",
      buyer_way: "buyerWay",
    },
    slug: "BuyerPurchaseDetailPDFView",
    title: "Buyer Purchase Detail (by Round)",
    showOnBuyerCard: true,
    getAllowRenderReport: isCommercialUser,
  },
  SaleOutwardMovementRecordPDFView: {
    fileType: "pdf",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: true,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isRulebookDriven: false,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate:
      "/reports/saleyards/{saleyardName}/sale-outward-movement-record/{livestockSaleId}/{buyerId}/",
    showToAgents: true,
    showToBuyers: true,
    showToSaleyardAdmins: true,
    showToVendors: false,
    queryMap: {
      buyer_way: "buyerWay",
      user_role: "userRole",
    },
    slug: "SaleOutwardMovementRecordPDFView",
    tab: "buyers",
    title: "Sale Outward Movement Record",
    showOnBuyerCard: true,
  },
  BuyerPurchaseCheckPageForBuyerPDFView: {
    fileType: "pdf",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: true,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isRulebookDriven: false,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate:
      "/reports/saleyards/{saleyardName}/buyer-purchase-check/{livestockSaleId}/{buyerId}/",
    showToAgents: true,
    showToBuyers: true,
    showToSaleyardAdmins: true,
    showToVendors: false,
    slug: "BuyerPurchaseCheckPageForBuyerPDFView",
    queryMap: {
      user_role: "userRole",
      buyer_way: "buyerWay",
    },
    tab: "buyers",
    title: "Buyer Purchase Check",
    showOnBuyerCard: true,
    getAllowRenderReport: isCommercialUser,
  },
  BuyerPurchaseCheckPageForBuyerPerBuyerWayPDFView: {
    fileType: "pdf",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: true,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isRulebookDriven: false,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate:
      "/reports/saleyards/{saleyardName}/buyer-purchase-check/{livestockSaleId}/{buyerId}/?page_per_buyer_way=True",
    showToAgents: true,
    showToBuyers: true,
    showToSaleyardAdmins: true,
    showToVendors: false,
    slug: "BuyerPurchaseCheckPageForBuyerPDFView",
    queryMap: {
      user_role: "userRole",
      buyer_way: "buyerWay",
    },
    tab: "buyers",
    title: "Buyer Purchase Check by Buyer Way",
    showOnBuyerCard: true,
    getAllowRenderReport: isCommercialUser,
  },
  BillingReport: {
    fileType: "csv",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: false,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isRulebookDriven: true,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate:
      "/reports/saleyards/{saleyardName}/billing-report/{livestockSaleId}/{rulebookId}/",
    showToAgents: true,
    showToBuyers: false,
    showToSaleyardAdmins: true,
    showToVendors: false,
    slug: "BillingReport",
    queryMap: { user_role: "userRole" },
    tab: "saleyard",
    title: "Billing Report",
  },
  ConsignmentBillingReportPdf: {
    fileType: "pdf",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: false,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isRulebookDriven: true,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate:
      "/reports/saleyards/{saleyardName}/consignment-billing-report-pdf/{livestockSaleId}/{rulebookId}/",
    showToAgents: true,
    showToBuyers: false,
    showToSaleyardAdmins: true,
    showToVendors: false,
    slug: "ConsignmentBillingReportPdf",
    queryMap: { user_role: "userRole" },
    tab: "saleyard",
    title: "Consignment Billing Report",
  },
  DeploymentBillingReportPdf: {
    fileType: "pdf",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: false,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isRulebookDriven: true,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate:
      "/reports/saleyards/{saleyardName}/deployment-billing-report-pdf/{livestockSaleId}/{rulebookId}/",
    showToAgents: true,
    showToBuyers: false,
    showToSaleyardAdmins: true,
    showToVendors: false,
    slug: "DeploymentBillingReportPdf",
    queryMap: { user_role: "userRole" },
    tab: "saleyard",
    title: "Deployment Billing Report",
  },
  DeploymentBillingReportWithSaleStatisticsPdf: {
    fileType: "pdf",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: false,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isRulebookDriven: true,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate:
      "/reports/saleyards/{saleyardName}/deployment-billing-report-pdf/{livestockSaleId}/{rulebookId}/",
    showToAgents: true,
    showToBuyers: false,
    showToSaleyardAdmins: true,
    showToVendors: false,
    slug: "DeploymentBillingReportWithSaleStatisticsPdf",
    queryMap: { user_role: "userRole", show_sale_statistics: true },
    tab: "saleyard",
    title: "Deployment Billing Report with Sale Statistics",
  },
  DebtorInsuranceTurnoverReportPdf: {
    fileType: "pdf",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: false,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isMultiSale: true,
    isRulebookDriven: true,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate: "/reports/debtor-insurance-turnover/",
    showToAgents: false,
    showToBuyers: false,
    showToSaleyardAdmins: false,
    showToVendors: false,
    slug: "DebtorInsuranceTurnoverReportPdf",
    queryMap: makeQueryParamsCamelCase([
      "user_role",
      "livestocksale-id__in",
      "livestocksale-date__gte",
      "livestocksale-date__lte",
      "livestocksale-sale_type__name__in",
      "livestocksale-sale_sub_type_id__in",
    ]),
    tab: null,
    title: "Debtor Insurance Turnover",
  },
  CommissionRelationshipsReport: {
    fileType: "pdf",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: false,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isMultiSale: true,
    isRulebookDriven: true,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate: "/reports/relationships/commission/",
    showToAgents: false,
    showToBuyers: false,
    showToSaleyardAdmins: false,
    showToVendors: false,
    slug: "CommissionRelationshipsReport",
    queryMap: makeQueryParamsCamelCase([
      "user_role",
      "livestocksale-id__in",
      "livestocksale-date__gte",
      "livestocksale-date__lte",
      "livestocksale-sale_type__name__in",
      "livestocksale-sale_sub_type_id__in",
      ReportQueryParam.relatedFrom,
      ReportQueryParam.relatedTo,
      ReportQueryParam.documentStatusIn,
      ReportQueryParam.outputFormat,
    ]),
    outputFormatOptions: [OutputFormat.PDF, OutputFormat.XLS],
    tab: null,
    title: "Commission Relationships",
  },
  AgencyEarningsReport: {
    fileType: "pdf",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: false,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isMultiSale: true,
    isRulebookDriven: false,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate: "/reports/agency_earnings/",
    showToAgents: true,
    showToBuyers: false,
    showToSaleyardAdmins: false,
    showToVendors: false,
    slug: "AgencyEarningsReport",
    queryMap: makeQueryParamsCamelCase([
      "user_role",
      "livestocksale-id__in",
      "livestocksale-date__gte",
      "livestocksale-date__lte",
      "livestocksale-sale_type__name__in",
      "livestocksale-sale_sub_type_id__in",
      ReportQueryParam.outputFormat,
    ]),
    outputFormatOptions: [OutputFormat.PDF, OutputFormat.XLS],
    tab: null,
    title: "Agency Earnings",
  },
  TransitInsuranceReportPdf: {
    fileType: "pdf",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: false,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isMultiSale: true,
    isRulebookDriven: true,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate: "/reports/transit-insurance/",
    showToAgents: false,
    showToBuyers: false,
    showToSaleyardAdmins: false,
    showToVendors: false,
    slug: "TransitInsuranceReportPdf",
    queryMap: makeQueryParamsCamelCase([
      "user_role",
      "livestocksale-id__in",
      "livestocksale-date__gte",
      "livestocksale-date__lte",
      "livestocksale-sale_type__name__in",
      "livestocksale-sale_sub_type_id__in",
    ]),
    tab: null,
    title: "Transit Insurance",
  },

  FederalLevyReportPdf: {
    fileType: "pdf",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: false,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isMultiSale: true,
    isRulebookDriven: true,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate: "/reports/federal-levy-pdf/",
    showToAgents: false,
    showToBuyers: false,
    showToSaleyardAdmins: false,
    showToVendors: false,
    slug: "FederalLevyReportPdf",
    queryMap: makeQueryParamsCamelCase([
      "user_role",
      "livestocksale-id__in",
      "livestocksale-date__gte",
      "livestocksale-date__lte",
      "livestocksale-sale_type__name__in",
      "livestocksale-sale_sub_type_id__in",
    ]),
    tab: null,
    title: "Federal Levies (PDF)",
  },

  FederalLevyReportCsv: {
    fileType: "csv",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: false,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isMultiSale: true,
    isRulebookDriven: true,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate: "/reports/federal-levy-csv/",
    showToAgents: false,
    showToBuyers: false,
    showToSaleyardAdmins: false,
    showToVendors: false,
    slug: "FederalLevyReportCsv",
    queryMap: makeQueryParamsCamelCase([
      "user_role",
      "livestocksale-id__in",
      "livestocksale-date__gte",
      "livestocksale-date__lte",
      "livestocksale-sale_type__name__in",
      "livestocksale-sale_sub_type_id__in",
    ]),
    tab: null,
    title: "Federal Levies (CSV)",
  },
  VicStateLevyReportPdf: {
    fileType: "pdf",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: false,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isMultiSale: true,
    isRulebookDriven: true,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate: "/reports/vic-state-levy/",
    showToAgents: false,
    showToBuyers: false,
    showToSaleyardAdmins: false,
    showToVendors: false,
    slug: "VicStateLevyReportPdf",
    queryMap: makeQueryParamsCamelCase([
      "user_role",
      "livestocksale-id__in",
      "livestocksale-date__gte",
      "livestocksale-date__lte",
      "livestocksale-sale_type__name__in",
      "livestocksale-sale_sub_type_id__in",
    ]),
    tab: null,
    title: "Vic State Revenue Office Levy",
  },
  SASheepIndustryFundReport: {
    fileType: "pdf",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: false,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isMultiSale: true,
    isRulebookDriven: true,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate: "/reports/sa-sheep-industry-fund-report/",
    showToAgents: false,
    showToBuyers: false,
    showToSaleyardAdmins: false,
    showToVendors: false,
    slug: "SASheepIndustryFundReport",
    queryMap: makeQueryParamsCamelCase([
      "user_role",
      "livestocksale-id__in",
      "livestocksale-date__gte",
      "livestocksale-date__lte",
      "livestocksale-sale_type__name__in",
      "livestocksale-sale_sub_type_id__in",
    ]),
    tab: null,
    title: "SA Sheep Industry Fund",
  },

  BulkBillingReport: {
    fileType: "txt",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: false,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isRulebookDriven: true,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate:
      "/reports/saleyards/{saleyardName}/bulk-billing-report/{livestockSaleId}/{rulebookId}/",
    showToAgents: true,
    showToBuyers: false,
    showToSaleyardAdmins: false,
    showToVendors: false,
    slug: "BulkBillingReport",
    queryMap: { user_role: "userRole" },
    tab: "saleyard",
    title: "Bulk Billing Report",
  },
  IndividualBidderDetailReport: {
    fileType: "pdf",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBidderDriven: true,
    isBuyerDriven: false,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isRulebookDriven: true,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate:
      "/reports/saleyards/{saleyardName}/bidder-detail-individual/{livestockSaleId}/{bidderId}/",
    showToAgents: true,
    showToBuyers: false,
    showToSaleyardAdmins: true,
    showToVendors: false,
    slug: "IndividualBidderDetailReport",
    queryMap: { user_role: "userRole" },
    tab: "saleyard",
    title: "Bidder Detail Report Individual",
  },
  IndividualBidderInvoiceReport: {
    fileType: "pdf",
    // isAgentDriven: false,
    isAgencyDriven: true,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBidderDriven: true,
    isBuyerDriven: false,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isRulebookDriven: true,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate:
      "/reports/saleyards/{saleyardName}/stud-buyer-tax-invoice/{livestockSaleId}/{buyerId}/{agencyShortName}/",
    showToAgents: true,
    showToBuyers: false,
    showToSaleyardAdmins: true,
    showToVendors: false,
    slug: "StudBuyerTaxInvoice",
    queryMap: { user_role: "userRole", buyer_way_id: "buyerWayId" },
    tab: "saleyard",
    title: "Stud Buyer Tax Invoice Individual",
  },
  DetailedFinancialStatementReport: {
    fileType: "pdf",
    // isAgentDriven: false,
    isAgencyDriven: true,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    // isBidderDriven: true,

    // isBuyerDriven: false,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isMultiSale: true,

    // isRulebookDriven: true,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    // pathTemplate: "/reports/detailed-financial-statement/{businessId}/",
    pathTemplate: "/reports/detailed-financial-statement/",
    showToAgents: true,
    showToBuyers: false,
    showToSaleyardAdmins: false,
    showToVendors: false,
    showBillingRunSummary: false,
    slug: "DetailedFinancialStatementReport",
    queryMap: makeQueryParamsCamelCase([
      "user_role",
      "start_date",
      "end_date",
      "business_id",
      ReportQueryParam.showStockDetails,
    ]),
    queryMapBulk: makeQueryParamsCamelCase([
      "business_id",
      "end_date",
      "start_date",
      "user_role",
    ]),
    tab: null,
    title: "Detailed Financial Statement",
  },
  FinancialStatementReport: {
    fileType: "pdf",
    // isAgentDriven: false,
    isAgencyDriven: true,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    // isBidderDriven: true,

    // isBuyerDriven: false,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isMultiSale: true,

    // isRulebookDriven: true,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    // pathTemplate: "/reports/financial-statement/{businessId}/",
    pathTemplate: "/reports/financial-statement/",
    showToAgents: true,
    showToBuyers: false,
    showToSaleyardAdmins: false,
    showToVendors: false,
    showBillingRunSummary: false,
    slug: "FinancialStatementReport",
    queryMap: makeQueryParamsCamelCase([
      "user_role",
      "start_date",
      "end_date",
      "business_id",
    ]),
    queryMapBulk: makeQueryParamsCamelCase([
      "business_id",
      "end_date",
      "start_date",
      "user_role",
    ]),
    tab: null,
    title: "Financial Statement",
  },

  FinancialStatementZipReport: {
    fileType: "zip",
    // isAgentDriven: false,
    isAgencyDriven: true,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    // isBidderDriven: true,

    // isBuyerDriven: false,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isMultiSale: true,

    // isRulebookDriven: true,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    // pathTemplate: "/reports/financial-statement/{businessId}/",
    pathTemplate: "/reports/financial-statement-zip/",
    showToAgents: true,
    showToBuyers: false,
    showToSaleyardAdmins: false,
    showToVendors: false,
    showBillingRunSummary: false,
    slug: "FinancialStatementZipReport",
    queryMap: makeQueryParamsCamelCase([
      "user_role",
      "start_date",
      "end_date",
      "business_id",
    ]),
    queryMapBulk: makeQueryParamsCamelCase([
      "business_id",
      "end_date",
      "start_date",
      "user_role",
    ]),
    tab: null,
    title: "Financial Statement (Zip)",
  },
  OutstandingStatementReport: {
    fileType: "pdf",
    // isAgentDriven: false,
    isAgencyDriven: true,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    // isBidderDriven: true,

    // isBuyerDriven: false,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isMultiSale: true,

    // isRulebookDriven: true,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    // pathTemplate: "/reports/outstanding-statement/{businessId}/",
    pathTemplate: "/reports/outstanding-statement/",
    showToAgents: true,
    showToBuyers: false,
    showToSaleyardAdmins: false,
    showToVendors: false,
    showBillingRunSummary: false,
    slug: "OutstandingStatementReport",
    queryMap: makeQueryParamsCamelCase([
      "user_role",
      "start_date",
      "end_date",
      "business_id",
      ReportQueryParam.showStockDetails,
    ]),
    queryMapBulk: makeQueryParamsCamelCase([
      "business_id",
      "end_date",
      "start_date",
      "user_role",
    ]),
    tab: null,
    title: "Outstanding Statement",
  },
  TransactionListReportPdf: {
    fileType: "pdf",
    // isAgentDriven: false,
    isAgencyDriven: true,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    // isBidderDriven: true,

    // isBuyerDriven: false,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isMultiSale: true,

    // isRulebookDriven: true,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    // pathTemplate: "/reports/transaction-list/{businessId}/",
    pathTemplate: "/reports/transaction-list/",
    showToAgents: true,
    showToBuyers: false,
    showToSaleyardAdmins: false,
    showToVendors: false,
    showBillingRunSummary: false,
    slug: "TransactionListReportPdf",
    queryMap: makeQueryParamsCamelCase([
      "user_role",
      "start_date",
      "end_date",
      "business_id",
    ]),
    queryMapBulk: makeQueryParamsCamelCase([
      "business_id",
      "end_date",
      "start_date",
      "user_role",
    ]),
    tab: null,
    title: "Transactions",
  },

  BusinessSummaryReport: {
    // Default is pdf - but could be csv
    fileType: "pdf",
    isAgentDriven: false,
    isAgencyDriven: false,
    isBranchAndAgencyDriven: false,
    isBuyerAndSaleRoundDriven: false,
    isBidderDriven: false,

    isBuyerDriven: false,
    isBuyerBuyerWayDriven: false,
    isBuyerDestinationPicDriven: false,
    isBuyerNameRangeDriven: false,
    isConsignedFromDriven: false,
    isDeploymentDriven: false,
    isMultiSale: true,
    isRulebookDriven: false,
    isSaleRoundDriven: false,
    isSaleRoundAndAgencyDriven: false,
    isVendorDriven: false,

    pathTemplate: "/reports/business-summary/",
    showToAgents: true,
    showToBuyers: false,
    showToSaleyardAdmins: true,
    showToVendors: false,
    showBillingRunSummary: false,
    slug: "BusinessSummaryReport",
    queryMap: makeQueryParamsCamelCase([
      "user_role",
      "start_date",
      "end_date",
      "group_bys",
      "only_sold",
      "species_id",
      ReportQueryParam.outputFormat,
    ]),
    tab: null,
    title: "Business Summary Report",
    outputFormatOptions: [OutputFormat.CSV, OutputFormat.PDF],
  },
  ClientAgingReport: {
    fileType: "pdf",
    isAgencyDriven: true,
    isMultiSale: true,
    pathTemplate: "/reports/client-aging/",
    showToAgents: true,
    showToBuyers: false,
    showToSaleyardAdmins: true,
    showToVendors: false,
    showBillingRunSummary: false,
    slug: "ClientAgingReport",
    queryMap: makeQueryParamsCamelCase([
      "user_role",
      ReportQueryParam.asAtDate,
      ReportQueryParam.buckets,
      ReportQueryParam.bucketCount,
      ReportQueryParam.groupBy,
      ReportQueryParam.orderBy,
      ReportQueryParam.layout,
      ReportQueryParam.clientType,
      ReportQueryParam.showContactDetails,
      ReportQueryParam.labelIds,
      ReportQueryParam.relationType,
      ReportQueryParam.relatedBusinessId,
      ReportQueryParam.outputFormat,
    ]),
    tab: null,
    title: "Client Aging Report",
    outputFormatOptions: [OutputFormat.PDF, OutputFormat.CSV],
  },
  RFIDListCSVView: {
    fileType: "csv",
    // isAgentDriven: false,
    // isAgencyDriven: false,
    // isBranchAndAgencyDriven: false,
    // isBuyerAndSaleRoundDriven: false,
    isBuyerDriven: true,
    // isBuyerBuyerWayDriven: false,
    // isBuyerDestinationPicDriven: false,
    // isBuyerNameRangeDriven: false,
    // isConsignedFromDriven: false,
    // isDeploymentDriven: false,
    isMultiSale: false,
    isRulebookDriven: false,
    // isSaleRoundDriven: false,
    // isSaleRoundAndAgencyDriven: false,
    // isVendorDriven: false,
    pathTemplate:
      "reports/saleyards/{saleyard_name}/rfid-list/{livestocksale_id}/{buyer_id}/",
    showToAgents: true,
    showToBuyers: true,
    showToSaleyardAdmins: true,
    showToVendors: false,
    slug: "RFIDListCSVView",
    queryMap: makeQueryParamsCamelCase(["user_role"]),
    tab: null,
    title: "RFID report (CSV)",
    allowAttachedToBillingDocumentEmail: true,
  },
};

export const MultiSaleReportOptions = sortByKey(
  Object.values(Reports)
    .filter(report => report.isMultiSale)

    .map(report => ({
      label: report.title,
      value: report.slug,
    })),
  "label",
);

export const BulkCreateReportOptions = sortByKey(
  Object.values(Reports)
    .filter(report => report.queryMapBulk !== undefined)

    .map(report => ({
      label: report.title,
      value: report.slug,
    })),
  "label",
);
